import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import type { FileAttachment } from 'api/attachments';
import { ExpenseApi } from 'api/expense-management/expenses/api';
import type {
  Expense,
  ExpenseResponse,
} from 'api/expense-management/expenses/types';
import type { ExpenseAccess } from 'api/expense-management/types';
import type { EntityToApprovalMapping } from 'components/organisms/invoice-settings/types';
import { GENERAL_API_STALE } from 'config/query-config';

export const useGetExpenseById = (
  expenseId: number,
  queryOptions?: UseQueryOptions<
    ExpenseResponse,
    unknown,
    ExpenseResponse,
    string[]
  >,
) => {
  return useQuery(
    ['expense', String(expenseId)],
    () => ExpenseApi.getExpenseById(expenseId),
    {
      staleTime: 0,
      cacheTime: 0,
      ...(queryOptions ?? {}),
    },
  );
};

export const useCreateExpense = () => {
  return useMutation({
    mutationFn: (expense: Omit<Expense, 'expenseId'>) =>
      ExpenseApi.createExpense(expense),
  });
};

export const useUpdateExpense = () => {
  return useMutation({
    mutationFn: (expense: Partial<Expense>) =>
      expense.expenseId
        ? ExpenseApi.updateExpense(expense.expenseId, expense)
        : Promise.reject(new Error('Expense ID is required')),
  });
};

export const useDeleteExpense = () => {
  return useMutation({
    mutationFn: (expenseId: number) => ExpenseApi.deleteExpense(expenseId),
  });
};

export const useExtractExpenseDetails = () => {
  return useMutation({
    mutationFn: (
      expenseDocuments: Array<Pick<FileAttachment, 'downloadUrl'>>,
    ) => ExpenseApi.extractExpenseDetails(expenseDocuments),
  });
};

export const useUpdateUserStatusForExpense = () => {
  return useMutation({
    mutationFn: ({
      expenseId,
      entityToApprovalMappingId,
      entityToApprovalMapping,
    }: {
      expenseId: number;
      entityToApprovalMappingId: number;
      entityToApprovalMapping: Pick<
        EntityToApprovalMapping,
        'approvalStatus' | 'notes'
      >;
    }) =>
      ExpenseApi.updateUserStatusForExpense(
        expenseId,
        entityToApprovalMappingId,
        entityToApprovalMapping,
      ),
  });
};

/**
 * This method is used to override the expense status for a given approval workflow level.
 * @description Override expense status
 */
export const useOverrideExpenseStatus = () => {
  return useMutation({
    mutationFn: ({
      expenseId,
      approvalWorkflowLevelId,
      entityToApprovalMapping,
    }: {
      expenseId: number;
      approvalWorkflowLevelId: number;
      entityToApprovalMapping: Pick<EntityToApprovalMapping, 'approvalStatus'>;
    }) =>
      ExpenseApi.overrideExpenseStatus(
        expenseId,
        approvalWorkflowLevelId,
        entityToApprovalMapping,
      ),
  });
};

/**
 * This method is used to perform approvals only when there's no approver attached to an expense.
 * That means there's no approval workflow levels attached to the expense & no entityToApprovalMapping is attached to the expense.
 * @description Force approval on an expense
 */
export const useForceApprovalOnExpense = () => {
  return useMutation({
    mutationFn: ({
      expenseId,
      entityToApprovalMapping,
    }: {
      expenseId: number;
      entityToApprovalMapping: Pick<EntityToApprovalMapping, 'approvalStatus'>;
    }) => ExpenseApi.forceApprovalOnExpense(expenseId, entityToApprovalMapping),
  });
};

export const useGetExpenseAuditLogs = (expenseId: number | undefined) => {
  return useQuery({
    queryKey: ['expense', expenseId, 'audit-logs'],
    queryFn: () =>
      expenseId ? ExpenseApi.getExpenseAuditLogs(expenseId) : undefined,
    enabled: !!expenseId,
    cacheTime: 0,
  });
};

export const EXPENSE_ACCESS_KEY = 'expense-access';

export const useExpenseAccessFlagsOrder = () => {
  return useQuery(
    [EXPENSE_ACCESS_KEY, 'expense-access-flags-order'],
    () => ExpenseApi.getExpenseAccessFlagsOrder(),
    {
      staleTime: GENERAL_API_STALE,
    },
  );
};

export const useGetExpenseAccess = (
  projectId?: number,
  userId?: number,
  queryOptions?: UseQueryOptions<
    ExpenseAccess,
    unknown,
    ExpenseAccess,
    string[]
  >,
) => {
  return useQuery(
    [EXPENSE_ACCESS_KEY, String(projectId), String(userId)],
    () => ExpenseApi.getExpenseAccess(projectId, userId),
    {
      staleTime: GENERAL_API_STALE,
      ...queryOptions,
    },
  );
};

export const useGetAccessibleExpenseProjects = () => {
  return useQuery({
    queryKey: [EXPENSE_ACCESS_KEY, 'project-access'],
    queryFn: () => ExpenseApi.getAccessibleProjects('CREATE'),
  });
};

export const useGetAccessibleExpenseUsers = (projectId?: number) => {
  return useQuery({
    queryKey: [EXPENSE_ACCESS_KEY, 'user-access', String(projectId)],
    queryFn: () => ExpenseApi.getAccessibleUsers('CREATE', projectId),
  });
};
